import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  CanMatch,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { Observable, of, pipe, UnaryFunction } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { Auth, user } from '@angular/fire/auth';
import { User } from 'firebase/auth';

import { isSystemAdminIdentity } from '../util/google-sso';

export type AuthPipeGenerator = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => AuthPipe;
export type AuthPipe = UnaryFunction<Observable<User | null>, Observable<boolean | string | any[]>>;

export const loggedIn: AuthPipe = map(user => !!user);

// copied manually from: https://github.com/angular/angularfire/blob/master/src/auth-guard/auth-guard.ts
// as currently not supported in modular mode without doing so
@Injectable({
  providedIn: 'any'
})
export class AuthGuard {
  constructor(private router: Router, private auth: Auth) { }

  canActivate = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const authPipeFactory = next.data['authGuardPipe'] as AuthPipeGenerator || (() => loggedIn);
    return user(this.auth).pipe(
      take(1),
      authPipeFactory(next, state),
      map(can => {
        if (typeof can === 'boolean') {
          return can;
        } else if (Array.isArray(can)) {
          return this.router.createUrlTree(can);
        } else {
          return this.router.parseUrl(can);
        }
      })
    );
  }

}

@Injectable({
  providedIn: 'root'
})
export class SystemAdminGuard implements CanActivate, CanActivateChild, CanMatch, CanLoad {
  constructor(private router: Router, private auth: Auth) { }

  canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.checkAccess();
  }

  canActivateChild(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.checkAccess();
  }

  canMatch(_route: Route, _segments: UrlSegment[]): Observable<boolean | UrlTree> {
    return this.checkAccess();
  }

  canLoad(_route: Route, _segments: UrlSegment[]): Observable<boolean | UrlTree> {
    return this.checkAccess();
  }

  private checkAccess(): Observable<boolean | UrlTree> {
    return user(this.auth).pipe(
      take(1),
      switchMap(currentUser => currentUser
        ? currentUser.getIdTokenResult().then(token => ({ currentUser, token }))
        : of(null)),
      map(result => isSystemAdminIdentity(
        result?.token.claims?.['isAdmin'],
        result?.token.claims?.['email'] ?? result?.currentUser.email,
      )
        ? true
        : this.router.createUrlTree(['/not-found'])),
      catchError(err => {
        console.error('SystemAdminGuard checkAccess error:', err);
        return of(this.router.createUrlTree(['/not-found']));
      })
    );
  }
}

export const canActivate = (pipe: AuthPipeGenerator) => ({
  canActivate: [AuthGuard], data: { authGuardPipe: pipe }
});

export const isNotAnonymous: AuthPipe = map(user => !!user && !user.isAnonymous);
export const idTokenResult = switchMap((user: User | null) => user ? user.getIdTokenResult() : of(null));
export const emailVerified: AuthPipe = map(user => !!user && user.emailVerified);
export const customClaims = pipe(idTokenResult, map(idTokenResult => idTokenResult?.claims ?? {}));
export const hasCustomClaim: (claim: string) => AuthPipe =
  (claim) => pipe(customClaims, map(claims => claims.hasOwnProperty(claim)));
export const redirectUnauthorizedTo: (redirect: string | any[]) => AuthPipe =
  (redirect) => pipe(loggedIn, map(loggedIn => loggedIn || redirect));
export const redirectLoggedInTo: (redirect: string | any[]) => AuthPipe =
  (redirect) => pipe(loggedIn, map(loggedIn => loggedIn && redirect || true));
export const isAdminClaim: AuthPipe = pipe(
  customClaims,
  map(claims => isSystemAdminIdentity(claims?.['isAdmin'], claims?.['email']))
);
export const redirectNonAdminTo: (redirect: string | any[]) => AuthPipe =
  (redirect) => pipe(isAdminClaim, map(isAdmin => isAdmin || redirect));
